class LanguageSelector {
  constructor(selector) {
    this.container = document.querySelector(selector);
    if (!this.container) return;
    
    this.button = this.container.querySelector('.language-selector__button');
    this.dropdown = this.container.querySelector('.language-selector__dropdown');
    this.form = this.container.querySelector('.localization-form');
    this.hiddenInput = this.form?.querySelector('input[name="language_code"]');
    this.isOpen = false;
    this.isInitialized = false;
    
    this.init();
  }
  
  init() {
    // Add a small delay to ensure smooth loading
    requestAnimationFrame(() => {
      this.setupInitialState();
      this.bindEvents();
      this.markAsLoaded();
    });
  }
  
  setupInitialState() {
    // Ensure dropdown is completely hidden initially
    this.closeDropdown();
    
    // Set initial ARIA states
    this.button.setAttribute('aria-expanded', 'false');
    this.dropdown.setAttribute('aria-hidden', 'true');
  }
  
  bindEvents() {
    // Toggle dropdown on button click
    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.toggleDropdown();
    });
    
    // Handle language selection
    this.dropdown.addEventListener('click', (e) => {
      const link = e.target.closest('.language-selector__link');
      if (link) {
        e.preventDefault();
        this.selectLanguage(link);
      }
    });
    
    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.container.contains(e.target)) {
        this.closeDropdown();
      }
    });
    
    // Close dropdown on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeDropdown();
        this.button.focus();
      }
    });
    
    // Handle keyboard navigation
    this.dropdown.addEventListener('keydown', (e) => {
      this.handleKeyboardNavigation(e);
    });
  }
  
  markAsLoaded() {
    // Add a slight delay before marking as loaded to prevent any flash
    setTimeout(() => {
      this.container.classList.add('js-loaded');
      this.isInitialized = true;
    }, 50); // Small delay to ensure CSS transitions are ready
  }
  
  toggleDropdown() {
    if (!this.isInitialized) return;
    
    if (this.isOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }
  
  openDropdown() {
    if (!this.isInitialized) return;
    
    this.isOpen = true;
    this.button.setAttribute('aria-expanded', 'true');
    this.dropdown.setAttribute('aria-hidden', 'false');
    
    // Use double requestAnimationFrame for smooth transition
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.dropdown.classList.add('is-open');
      });
    });
    
    // Focus first available language option
    const firstLink = this.dropdown.querySelector('.language-selector__link');
    if (firstLink) {
      setTimeout(() => firstLink.focus(), 150);
    }
  }
  
  closeDropdown() {
    this.isOpen = false;
    this.button.setAttribute('aria-expanded', 'false');
    this.dropdown.setAttribute('aria-hidden', 'true');
    this.dropdown.classList.remove('is-open');
  }
  
  selectLanguage(link) {
    const languageCode = link.dataset.value;
    if (this.hiddenInput && this.form && languageCode) {
      // Add loading state
      this.container.classList.add('is-loading');
      
      // Close dropdown immediately
      this.closeDropdown();
      
      this.hiddenInput.value = languageCode;
      this.form.submit();
    }
  }
  
  handleKeyboardNavigation(e) {
    const links = Array.from(this.dropdown.querySelectorAll('.language-selector__link'));
    const currentIndex = links.indexOf(document.activeElement);
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        const nextIndex = (currentIndex + 1) % links.length;
        links[nextIndex].focus();
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        const prevIndex = (currentIndex - 1 + links.length) % links.length;
        links[prevIndex].focus();
        break;
        
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (document.activeElement && document.activeElement.classList.contains('language-selector__link')) {
          this.selectLanguage(document.activeElement);
        }
        break;
    }
  }
}

// Initialize with proper timing
function initLanguageSelector() {
  // Add a small delay to ensure all styles are loaded
  setTimeout(() => {
    new LanguageSelector('.language-selector');
  }, 100);
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLanguageSelector);
} else {
  // DOM is already ready, but still add a small delay
  initLanguageSelector();
} 